import type { RewardType } from '../rewards';

export const MissionType = {
  WEEKLY: 'weekly',
  ONBOARDING: 'onboarding',
} as const;

export type MissionType = (typeof MissionType)[keyof typeof MissionType];

export const MissionStatus = {
  ACTIVE: 'active',
  COMPLETED: 'completed',
  EXPIRED: 'expired',
} as const;

export type MissionStatus = (typeof MissionStatus)[keyof typeof MissionStatus];

export const MissionTaskType = {
  COMPLETE_ROOMS: 'completeRooms',
  COMPLETE_QUESTIONS: 'completeQuestions',
  EARN_POINTS: 'earnPoints',
  START_VM: 'startVm',
  STREAK_DAYS: 'streakDays',
} as const;

export type MissionTaskType = (typeof MissionTaskType)[keyof typeof MissionTaskType];

// CORE INTERFACES (Backend-focused)

export interface GeneratedTask {
  type: MissionTaskType;
  value: boolean | number;
}

export interface TaskValueRange {
  min: number;
  max: number;
}

export interface TaskConfig {
  type: 'boolean' | 'number';
  range?: TaskValueRange;
}

// Mission document without MongoDB-specific fields (safe for frontend)
export interface SafeMissionDocument {
  readonly type: MissionType;
  readonly tasks: Partial<Record<MissionTaskType, boolean | number>>;
  readonly progress: Partial<Record<MissionTaskType, boolean | number>>;
  readonly status: MissionStatus;
  readonly created: Date;
  readonly completedAt: Date | null;
  readonly reward: RewardType | null;
  readonly weekEnd: Date;
  readonly weekStart: Date;
}

// FRONTEND-SPECIFIC INTERFACES (Transformed for UI consumption)

export interface TransformedMissionTask {
  readonly type: MissionTaskType;
  readonly count: number;
  readonly countCompleted: number;
}

export interface TransformedMissionData {
  readonly type: MissionType;
  readonly tasks: readonly TransformedMissionTask[];
  readonly status: MissionStatus;
  readonly created: Date;
  readonly completedAt: Date | null;
  readonly reward: RewardType | null;
  readonly weekEnd: Date;
  readonly weekStart: Date;
}

export interface WeeklyMissionResponse {
  readonly type: MissionType;
  readonly tasks: Partial<Record<MissionTaskType, boolean | number>>;
  readonly progress: Partial<Record<MissionTaskType, boolean | number>>;
  readonly status: MissionStatus;
  readonly created: string;
  readonly completedAt: string | null;
  readonly reward: RewardType | null;
  readonly weekEnd: string;
  readonly weekStart: string;
}

// UI COMPONENT INTERFACES

export interface MissionTaskProps {
  readonly title: string;
  readonly count: number;
  readonly countCompleted: number;
  readonly isDarkMode: boolean;
  readonly type: MissionTaskType;
}
